import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

const TABS = [
  { key: 'live',     label: 'Live' },
  { key: 'pending',  label: 'Pending' },
  { key: 'draft',    label: 'Draft' },
  { key: 'rejected', label: 'Rejected' },
];

const BADGE = {
  all:      { bg: 'rgba(100,116,139,0.12)', color: '#64748b' },
  draft:    { bg: '#e2e8f0',               color: '#475569' },
  pending:  { bg: '#fef3c7',               color: '#d97706' },
  live:     { bg: '#dcfce7',               color: '#16a34a' },
  rejected: { bg: '#fee2e2',               color: '#dc2626' },
};

// items: full unfiltered list. tab: 'all'|'draft'|'pending'|'live'|'rejected'.
// 'live' maps to approvalStatus === 'approved'. Items without approvalStatus default to 'draft'.
export function filterByStatus(items, tab, statusKey = 'approvalStatus') {
  if (tab === 'all') return items;
  const target = tab === 'live' ? 'approved' : tab;
  return items.filter((i) => (i[statusKey] || 'draft') === target);
}

// variant='dark'  → white text on blue background (Booth, Charity, PortalLinks)
// variant='light' → dark text on white background (Advertisers, Items, Campaigns, Ads)
// highlightTab/highlightToken: briefly flash a tab (e.g. after create/submit/withdraw) so the
// user can see where the item landed without forcing a tab switch. Bump highlightToken to
// re-trigger the same tab (a plain highlightTab change already retriggers via the effect dep).
export default function StatusTabs({ items = [], counts: countsProp, value, onChange, variant = 'light', statusKey = 'approvalStatus', sx, highlightTab, highlightToken }) {
  const counts = useMemo(() => countsProp ?? ({
    all:      items.length,
    draft:    items.filter((i) => !i[statusKey] || i[statusKey] === 'draft').length,
    pending:  items.filter((i) => i[statusKey] === 'pending').length,
    live:     items.filter((i) => i[statusKey] === 'approved').length,
    rejected: items.filter((i) => i[statusKey] === 'rejected').length,
  }), [items, statusKey, countsProp]);

  const isDark = variant === 'dark';

  const [pulseTab, setPulseTab] = useState(null);
  useEffect(() => {
    if (!highlightTab) return;
    setPulseTab(highlightTab);
    const timer = setTimeout(() => setPulseTab(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightTab, highlightToken]);

  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Tabs
        value={value}
        onChange={(_, v) => onChange(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: 36,
          ...(isDark
            ? {
                '& .MuiTabs-indicator': { backgroundColor: 'white' },
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.55)', minHeight: 36, py: 0.75, fontSize: 13, fontWeight: 600, textTransform: 'none' },
                '& .Mui-selected': { color: 'white !important' },
                '& .MuiTabScrollButton-root': { color: 'rgba(255,255,255,0.6)' },
              }
            : {
                '& .MuiTabs-indicator': { backgroundColor: '#0a326d' },
                '& .MuiTab-root': { color: '#64748b', minHeight: 36, py: 0.75, fontSize: 13, fontWeight: 600, textTransform: 'none' },
                '& .Mui-selected': { color: '#0a326d !important' },
              }),
        }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  borderRadius: '8px', px: 0.75, py: 0.25, mx: -0.75, my: -0.25,
                  animation: pulseTab === tab.key ? 'gms-tab-flash 1.5s ease-out' : 'none',
                  '@keyframes gms-tab-flash': {
                    '0%':   { backgroundColor: 'rgba(217,119,6,0.35)' },
                    '70%':  { backgroundColor: 'rgba(217,119,6,0.35)' },
                    '100%': { backgroundColor: 'rgba(217,119,6,0)' },
                  },
                }}
              >
                {tab.label}
                <Box
                  component="span"
                  sx={{
                    fontSize: 10, fontWeight: 700, lineHeight: 1.6,
                    px: 0.9, py: 0.1, borderRadius: '10px', minWidth: 18, textAlign: 'center',
                    bgcolor: BADGE[tab.key].bg,
                    color:   BADGE[tab.key].color,
                    transform: pulseTab === tab.key ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {counts[tab.key]}
                </Box>
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
}
